// src/config/pricing.js
//
// Stripe Payment Link configuration for the Scene Analysis unlock.
//
// -----------------------------------------------------------------------
// HOW THIS WORKS (and its honest limitation)
//
// This uses a Stripe PAYMENT LINK, which needs no server: Stripe hosts the
// entire checkout. The flow is:
//
//   1. User clicks "Unlock for $5" → we send them to PAYMENT_LINK.
//   2. Stripe takes payment on its own page.
//   3. Stripe redirects back to PRICING.successPath (/unlock), which grants
//      the unlock and returns the user to where they were.
//
// THE LIMITATION, STATED PLAINLY: with no server there is no cryptographic
// proof of payment on return — the unlock is granted because the browser
// arrived at the success URL. A determined person could reach that URL
// without paying. This is the same class of "soft gate" your access.js
// shared-code mode already documents: it stops casual access, not someone
// actively trying. For a $5 convenience unlock on settings the engine can
// compute anyway, that is a reasonable trade. If you need a HARD gate,
// switch to Stripe Checkout Sessions + a webhook on a real server (e.g. a
// Cloudflare Function), verify the session server-side, and issue a signed
// token here instead of trusting the return URL.
// -----------------------------------------------------------------------

export const PRICING = {
  enabled: true,

  price: "$5",
  priceCurrency: "USD",
  productName: "Scene Analysis",
  blurb:
    "Unlock on-device scene analysis: measure the actual light and your pan speed with the phone's own sensors, and compare them against what each recipe assumed.",

  // PLACEHOLDER — replace with your real Stripe Payment Link.
  //   Stripe Dashboard → Products → Payment Links → create a $5 link.
  //   Set the link's "After payment" redirect to:  https://YOURDOMAIN/unlock
  //   (Stripe appends its own session id; we also append our own marker.)
  paymentLink: "https://buy.stripe.com/PLACEHOLDER",

  // Where Stripe returns the buyer. The /unlock route reads this and grants
  // access. Keep in sync with the redirect you set on the Payment Link.
  successPath: "/unlock",

  // What the unlock is stored under, and how long it survives. "persistent"
  // (localStorage) is right for a paid unlock — it should outlast the tab,
  // unlike the on-set QR code which is session-only.
  storageKey: "unlock:scene-analysis",
  persistence: "persistent",
};

/**
 * Build the outbound Payment Link, carrying the page the user came from so
 * /unlock can send them back after granting access. Stripe passes through
 * the `client_reference_id` and any query already on the link.
 */
export function paymentLinkFor(returnTo) {
  const base = PRICING.paymentLink;
  if (!base || base.includes("PLACEHOLDER")) return base;
  try {
    const u = new URL(base);
    // Stripe echoes client_reference_id back in the dashboard; we also use
    // it as a lightweight "where to go back to" hint via the success URL's
    // own query when configured. Kept short and non-sensitive.
    if (returnTo) u.searchParams.set("client_reference_id", encodeURIComponent(returnTo));
    return u.toString();
  } catch (e) {
    return base;
  }
}
